import { nanoid } from 'nanoid';
import { IndexedDBFactory, IndexedDBManager } from '../../utils/IndexedDBManager';
import { FileUploader, IUploadCompleteInfo } from '../../notes/generic-notes/attachments/FileUploader';
import { arrayBufferToFile, getExtensionFromMimeType } from '../../utils/Audio';
import dayjs from 'dayjs';
import { BufferedEventBus } from '../../utils/BufferedEventBus';
import { IRecording, IRecordingData } from './RecordingState';
import { CaremapNotesModelFactory } from '../../notes/model/CaremapNotesModelFactory';
import { AlertPresenter } from '../../AlertBinder';
import { Logger } from '../../support/Logger';

export const RECORDING_SERVICE_STATUS_EVENT = 'RecordingService.status-updated';
export class RecordingService {
    static inject = () => [
        IndexedDBFactory,
        FileUploader,
        BufferedEventBus,
        CaremapNotesModelFactory,
        AlertPresenter,
        Logger
    ];

    private recordingDB: IndexedDBManager<IRecording>;

    constructor(
        private indexedDBFactory: IndexedDBFactory<IRecording>,
        private uploader: FileUploader,
        private events: BufferedEventBus,
        private notesFactory: CaremapNotesModelFactory,
        private alert: AlertPresenter,
        private logger: Logger
    ) {
        // get or create the IndexedDB for innerhive
        this.recordingDB = this.indexedDBFactory.get('innerhive', 'recordings');
    };

    add = async (recording: IRecordingData, extras: {skipAIProcessing?: boolean; appointmentId?: string}) => {
        const id = nanoid();
        const status = 'recording-complete';

        const noteVM = this.notesFactory.createFormForNewNote(
            { section: 'patient' },
            { title: 'Audio Note' }
        );

        const recordToAdd: IRecording = {
            ...recording,
            id,
            status,
            url: '',
            skipAIProcessing: extras.skipAIProcessing,
            appointmentId: extras.appointmentId,
            noteId: noteVM.getNote().noteId,
            byteLength: recording.file.byteLength,
            created: dayjs().utc().toISOString(),
            fileName: `${(new Date()).toLocaleDateString()} Recording.${getExtensionFromMimeType(recording.mimeType)}`
        };

        const dbRecordKey = await this.recordingDB.add(recordToAdd);

        // set the recording in the otherAttributes bag
        // save the note
        // then we try uploading
        const note = noteVM.getNote();
        note.otherAttributes.recording = {...recordToAdd};

        // remove the file ArrayBuffer
        delete note.otherAttributes.recording.file;
        noteVM.setNote(note);

        // wait on the save, otherwise the upload can happen faster
        // than the note is actually created and returned to the client
        try {
            // TODO: if the note doesn't save (OFFLINE??), we don't want to move on?
            // TODO: DISCUSS - and... everything is based on the Note, so if there isn't a Note, how do we show the user there's a recording???
            //      maybe in Recordings tab is where we use both Note state and RecordingState to display things??
            await noteVM.save({
                trackingFrom: 'Recording'
            });
        } catch (error) {
            // log to Sentry
            this.alert.showAndLogError(error);
        }

        // if successfully added to db, start the upload
        if (dbRecordKey) {
            this.upload(recordToAdd);
        }

        return recordToAdd;
    };

    get = async (id: IDBValidKey) => {
        return await this.recordingDB.get(id);
    };

    getAll = async () => {
        return await this.recordingDB.getAll();
    };

    delete = (id: IDBValidKey) => {
        this.recordingDB.delete(id);
    };

    upload = async (record: IRecording) => {
        const newRecord: IRecording = {...record, ...{status: 'uploading'}};

        await this.recordingDB.update(newRecord);
        this.events.emit(RECORDING_SERVICE_STATUS_EVENT, newRecord);

        // try uploading 3 times, then stop trying and change status to 'upload-failed'
        const maxAttempts = 3;
        const attemptUpload = async (attempt = 0) => {
            if (attempt < maxAttempts) {
                const mimeType = newRecord.mimeType;
                try {
                    await this.uploader.upload(
                        [arrayBufferToFile(
                            newRecord.file,
                            newRecord.fileName,
                            mimeType
                        )],
                        (fileInfo: IUploadCompleteInfo) => {
                            const uploadedRecord: IRecording = {...newRecord, ...{status: 'uploaded', url: fileInfo.update.url || ''}};

                            this.recordingDB.update(uploadedRecord);

                            // there is a fileInfo.size if we want, or can get it out of uploadedRecord.file ArrayBuffer
                            this.events.emit(RECORDING_SERVICE_STATUS_EVENT, uploadedRecord);
                        },
                        (failedFile: File) => {
                            attemptUpload(++attempt);

                            // log to Sentry if we tried 3 times to no avail
                            if (attempt === maxAttempts) {
                                // TODO: probably should be a custom alert, and a button to navigate to the
                                //      Record tab on home, so they see and can click the Retry button
                                //this.alert.showAndLogError(new Error('failed to upload new recording after 3 attempts'));
                                this.logger.error('failed to upload new recording after 3 attempts');
                            }
                        }
                    );
                } catch (error) {
                    // log to Sentry
                    this.alert.showAndLogError(error);
                }
            } else {
                // failed after 3 attempts, update record
                const failedRecord: IRecording = {...record, ...{status: 'upload-failed'}};
                await this.recordingDB.update(failedRecord);
                this.events.emit(RECORDING_SERVICE_STATUS_EVENT, failedRecord);
            }
        };

        attemptUpload();
    };
};
